import type { ReactNode } from 'react'
import { LoaderCircle, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

export function PageHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className='flex flex-col gap-4 border-b pb-6 sm:flex-row sm:items-end sm:justify-between'>
      <div className='max-w-2xl'>
        {eyebrow && (
          <p className='mb-2 text-xs font-bold tracking-[0.18em] text-[var(--tenant-brand)] uppercase'>
            {eyebrow}
          </p>
        )}
        <h1 className='text-2xl font-black tracking-tight sm:text-3xl'>
          {title}
        </h1>
        <p className='mt-2 text-sm leading-6 text-muted-foreground'>
          {description}
        </p>
      </div>
      {action}
    </div>
  )
}

export function LoadingState({ label = 'Loading' }: { label?: string }) {
  return (
    <div className='grid min-h-48 place-items-center text-sm text-muted-foreground'>
      <span className='inline-flex items-center gap-2'>
        <LoaderCircle className='size-4 animate-spin' />
        {label}
      </span>
    </div>
  )
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <Card className='border-dashed'>
      <CardContent className='grid min-h-56 place-items-center p-8 text-center'>
        <div className='max-w-sm'>
          <span className='mx-auto mb-4 grid size-11 place-items-center rounded-xl bg-muted'>
            <Plus className='size-5 text-muted-foreground' />
          </span>
          <h2 className='font-semibold'>{title}</h2>
          <p className='mt-2 text-sm leading-6 text-muted-foreground'>
            {description}
          </p>
          {action && <div className='mt-5'>{action}</div>}
        </div>
      </CardContent>
    </Card>
  )
}

export function StatusPill({
  value,
  tone,
}: {
  value: string
  tone?: 'good' | 'warning' | 'danger' | 'neutral'
}) {
  const inferred =
    tone ??
    (['ACTIVE', 'READY', 'ONLINE', 'PUBLISHED', 'SENT'].includes(value)
      ? 'good'
      : ['PAST_DUE', 'DEGRADED', 'PROCESSING', 'TRIALING'].includes(value)
        ? 'warning'
        : ['FAILED', 'OFFLINE', 'REJECTED', 'UNPAID'].includes(value)
          ? 'danger'
          : 'neutral')
  return (
    <span
      className={cn(
        'inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold tracking-wide',
        inferred === 'good' &&
          'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
        inferred === 'warning' &&
          'bg-amber-500/10 text-amber-700 dark:text-amber-400',
        inferred === 'danger' && 'bg-red-500/10 text-red-700 dark:text-red-400',
        inferred === 'neutral' && 'bg-muted text-muted-foreground'
      )}
    >
      {value.replaceAll('_', ' ')}
    </span>
  )
}

export function TableShell({
  headers,
  children,
}: {
  headers: string[]
  children: ReactNode
}) {
  return (
    <div className='overflow-hidden rounded-xl border bg-card'>
      <div className='overflow-x-auto'>
        <table className='w-full min-w-[680px] text-sm'>
          <thead className='bg-muted/60 text-left text-xs tracking-wide text-muted-foreground uppercase'>
            <tr>
              {headers.map((header) => (
                <th key={header} className='px-4 py-3 font-semibold'>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='divide-y'>{children}</tbody>
        </table>
      </div>
    </div>
  )
}

export function DialogActions({
  busy,
  submitLabel = 'Save',
  onCancel,
}: {
  busy: boolean
  submitLabel?: string
  onCancel: () => void
}) {
  return (
    <div className='flex justify-end gap-2 pt-4'>
      <Button type='button' variant='outline' onClick={onCancel}>
        Cancel
      </Button>
      <Button disabled={busy}>{busy ? 'Saving…' : submitLabel}</Button>
    </div>
  )
}
